import { z } from 'zod';
import { Dateish } from './common';

export const EmailTemplateSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  subject: z.string(),
  /** HTML com placeholders `{{ticket.number}}`, `{{client.name}}`, … */
  body: z.string(),
  active: z.boolean(),
  createdAt: Dateish,
  updatedAt: Dateish,
});
export type EmailTemplate = z.infer<typeof EmailTemplateSchema>;

export const TemplateBodySchema = z.object({
  name: z.string().min(1),
  subject: z.string().min(1),
  body: z.string().min(1),
  active: z.boolean().optional(),
});
export type TemplateBody = z.infer<typeof TemplateBodySchema>;

/**
 * `suppressed` é status PRÓPRIO e não um `skipped` reaproveitado: os dois
 * significam "não saiu", mas por motivos que precisam ser distinguíveis numa
 * auditoria. `skipped` = MAIL_MODE=off, uma decisão de configuração nossa;
 * `suppressed` = o destinatário está na nossa lista de supressão (bounce
 * permanente ou reclamação de spam) e mandar de novo seria exatamente o que
 * arruína a reputação do domínio. Confundir os dois apagaria a prova de que o
 * feedback loop está funcionando.
 */
export const EmailStatusSchema = z.enum(['sent', 'failed', 'skipped', 'suppressed']);
export type EmailStatus = z.infer<typeof EmailStatusSchema>;

export const SentEmailSchema = z.object({
  id: z.number().int(),
  toEmail: z.string(),
  subject: z.string(),
  ticketNumber: z.number().int().nullable(),
  templateId: z.number().int().nullable(),
  status: EmailStatusSchema,
  error: z.string().nullable(),
  createdAt: Dateish,
});
export type SentEmail = z.infer<typeof SentEmailSchema>;

/** GET /mail/log?ticket_number= */
export const MailLogQuerySchema = z.object({
  ticket_number: z.coerce.number().int().positive().optional(),
});
export type MailLogQuery = z.infer<typeof MailLogQuerySchema>;

/**
 * Tipo ESTRUTURAL do MailerPort do módulo mail da API (DESIGN-V3 §5),
 * declarado aqui (TIPOS apenas, zero runtime) para os módulos consumidores
 * (automations/csat/notifications) não redeclararem cada um a sua cópia —
 * módulo A não importa módulo B, mas todos importam o shared. O composition
 * root injeta a implementação real; a compatibilidade é por estrutura.
 *
 * Contrato herdado do MailerPort: `send` NUNCA lança para o chamador (loga e
 * registra failed em sent_emails).
 */
export interface MailerSendInput {
  to: string[];
  subject: string;
  html: string;
  /** Referência solta para o log (sent_emails.ticket_number). */
  ticketNumber?: number;
  /** Referência solta para o log (sent_emails.template_id). */
  templateId?: number;
  /**
   * O envio acontece FORA do withTenant (outbox pós-transação): o mailer usa
   * este tenantId para abrir escopo curto próprio só para o log em sent_emails.
   */
  tenantId?: number;
}

export interface MailerLike {
  send(input: MailerSendInput): Promise<void>;
}
